import { useMutation, useQueryClient } from '@tanstack/react-query';
import { http } from '@/lib/ats-https';
import { assertResolvedAtsNotHttpError } from '@/api/ats/atsEnvelope';
import type {
  AnonymizeResponse,
  BulkRequestConsentsPayload,
  ConsentLifecycleResponse,
  ConsentTarget,
  ExtendConsentPayload,
} from './types';

const BASE_URL = '/candidate_consents';

// The ATS client resolves 4xx as a fulfilled promise with `{ status, errors }`,
// so every mutation must assert before returning — otherwise onSuccess fires on
// a 422 and shows a false success toast.
const post = async <T>(url: string, body: unknown): Promise<T> => {
  const res = await http.post(url, body);
  assertResolvedAtsNotHttpError(res);
  return res as T;
};

export type ConsentLifecycleAction =
  | 'request'
  | 'remind'
  | 'mark_granted'
  | 'mark_not_required'
  | 'withdraw';

const targetBody = (target: ConsentTarget) =>
  'contactId' in target
    ? { contact_id: target.contactId }
    : { ats_application_id: target.atsApplicationId };

const postLifecycleAction = (
  action: ConsentLifecycleAction,
  target: ConsentTarget
): Promise<ConsentLifecycleResponse> =>
  post(`${BASE_URL}/${action}`, targetBody(target));

const postExtend = (
  target: ConsentTarget,
  payload: ExtendConsentPayload
): Promise<ConsentLifecycleResponse> =>
  post(`${BASE_URL}/extend`, { ...targetBody(target), ...payload });

// Bulk "request consent" (formerly "renew"). Dispatches an async bulk
// activity (request_consent, enum 25); per-item skips surface via the tracker.
const postBulkRequest = ({
  contactIds,
  atsApplicationIds,
}: BulkRequestConsentsPayload): Promise<unknown> =>
  post(`${BASE_URL}/bulk_request`, {
    ...(contactIds?.length && { contact_ids: contactIds }),
    ...(atsApplicationIds?.length && {
      ats_application_ids: atsApplicationIds,
    }),
  });

const postAnonymize = (
  target: ConsentTarget,
  confirm?: boolean
): Promise<AnonymizeResponse> =>
  post(`${BASE_URL}/anonymize`, {
    ...targetBody(target),
    // Wire key is confirm_active_applications (BE contract delta 2 §1); the old
    // `confirm` key is now ignored server-side.
    ...(confirm && { confirm_active_applications: confirm }),
  });

export const CONSENT_ACTIONS_API = {
  postLifecycleAction,
  postExtend,
  postBulkRequest,
  postAnonymize,
};

// Surfaces pass the query keys holding the consent block they render from
// (e.g. the drawer's application query) so the indicator refreshes in place.
type HookOptions = { invalidateKeys?: unknown[][] };

const useInvalidate = (invalidateKeys?: unknown[][]) => {
  const queryClient = useQueryClient();
  return () =>
    invalidateKeys?.forEach((queryKey) =>
      queryClient.invalidateQueries({ queryKey })
    );
};

export const useConsentLifecycleAction = ({
  invalidateKeys,
}: HookOptions = {}) => {
  const invalidate = useInvalidate(invalidateKeys);
  return useMutation({
    mutationFn: ({
      action,
      target,
    }: {
      action: ConsentLifecycleAction;
      target: ConsentTarget;
    }) => postLifecycleAction(action, target),
    onSuccess: invalidate,
  });
};

export const useBulkRequestConsents = ({
  invalidateKeys,
}: HookOptions = {}) => {
  const invalidate = useInvalidate(invalidateKeys);
  return useMutation({
    mutationFn: (payload: BulkRequestConsentsPayload) =>
      postBulkRequest(payload),
    onSuccess: invalidate,
  });
};

export const useAnonymizeCandidate = ({ invalidateKeys }: HookOptions = {}) => {
  const invalidate = useInvalidate(invalidateKeys);
  return useMutation({
    mutationFn: ({
      target,
      confirm,
    }: {
      target: ConsentTarget;
      confirm?: boolean;
    }) => postAnonymize(target, confirm),
    onSuccess: invalidate,
  });
};
